import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heartbeat } from 'phosphor-react';
import usePresence from '../use/usePresence';
import Aviso from './ui/Aviso';
import PrimaryButton from './buttons/PrimaryButton';

interface SensitiveDataConsentGateProps {
  aberto: boolean;
  onAutorizar: () => void;
  onAgoraNao: () => void;
  loading: boolean;
  error: string;
}

/**
 * O que a autorização cobre, em itens curtos. É o texto "específico e destacado" que a LGPD
 * pede para dado de saúde (art. 11, I) e para a transferência ao exterior (art. 33, VIII) —
 * por isso tela própria, e não uma linha a mais nos Termos.
 */
const ITENS: string[] = [
  'Para montar e ajustar sua rotina, o AutoCare trata dados de saúde da sua pele: as respostas da anamnese (inclusive alergias e o que você contar sobre você), fotos da pele, reações às etapas, anotações e as conversas com a assistente.',
  'Esses dados são enviados ao Google Gemini, com servidores fora do Brasil, para gerar recomendações e analisar suas fotos — inclusive conferir se duas fotos são da mesma pessoa. O Google não usa esses dados para treinar modelos.',
  'Não vendemos seus dados nem os usamos para anúncios.',
  'Você pode revogar quando quiser em Configurações › Privacidade. Revogar apaga a anamnese, as fotos, os registros e as conversas.',
  'Sem esta autorização, anamnese, registros de pele, progresso e a assistente ficam indisponíveis. Conta, rotinas, produtos e assinatura continuam funcionando.',
];

/**
 * Portão da autorização dos dados de saúde. Como o `TermsGate`, não fecha no clique fora nem
 * no X: daqui se sai autorizando ou dizendo "agora não" — que leva para Configurações, onde dá
 * para autorizar depois, baixar os dados ou excluir a conta.
 */
export default function SensitiveDataConsentGate({
  aberto,
  onAutorizar,
  onAgoraNao,
  loading,
  error,
}: SensitiveDataConsentGateProps) {
  const [marcado, setMarcado] = useState<boolean>(false);
  const { montado, estado } = usePresence(aberto, 200);

  if (!montado) return null;

  return (
    <div
      data-state={estado}
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 data-[state=closed]:pointer-events-none"
    >
      <div
        data-state={estado}
        className="fixed inset-0 bg-black/40 backdrop-blur-sm data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out"
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="consent-gate-title"
        data-state={estado}
        className="relative w-full max-w-md max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-soft-lg dark:shadow-none dark:border dark:border-gray-700 p-6 data-[state=open]:animate-sheet-in data-[state=closed]:animate-sheet-out sm:data-[state=open]:animate-scale-in sm:data-[state=closed]:animate-scale-out"
      >
        <div className="w-12 h-12 mb-4 bg-blush-100 dark:bg-blush-900/30 rounded-2xl flex items-center justify-center">
          <Heartbeat className="w-6 h-6 text-blush-500" />
        </div>

        <h2 id="consent-gate-title" className="text-lg font-semibold text-gray-800 dark:text-gray-100">
          Autorização para dados de saúde da pele
        </h2>
        <p className="mt-2 text-sm text-nude-500 dark:text-gray-400">
          Antes de continuar, leia o que acontece com estes dados. Eles são sensíveis pela lei, e por
          isso pedimos uma autorização só para eles.
        </p>

        <ul className="mt-4 flex flex-col gap-2">
          {ITENS.map((item) => (
            <li key={item} className="flex gap-2 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
              <span className="text-blush-400" aria-hidden>
                •
              </span>
              {item}
            </li>
          ))}
        </ul>

        <label className="mt-5 flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={marcado}
            onChange={(e) => setMarcado(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-nude-300 dark:border-gray-600 text-blush-500 focus:ring-blush-500 cursor-pointer"
          />
          <span className="text-xs leading-relaxed text-nude-500 dark:text-gray-400">
            Tenho 18 anos ou mais e autorizo o tratamento dos meus dados de saúde da pele, inclusive
            fotos, e o envio deles ao Google Gemini para as finalidades acima, como descrito na{' '}
            <Link
              to="/privacidade"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blush-500 font-semibold hover:text-blush-600 transition-colors"
            >
              Política de Privacidade
            </Link>
            .
          </span>
        </label>

        <Aviso mensagem={error} className="mt-3" />

        <div className="mt-5 flex flex-col gap-2">
          <PrimaryButton fullWidth disabled={!marcado} loading={loading} onClick={onAutorizar}>
            Autorizar
          </PrimaryButton>
          <button
            onClick={onAgoraNao}
            disabled={loading}
            className="py-2 text-sm text-nude-500 dark:text-gray-400 font-medium hover:text-nude-600 transition-colors"
          >
            Agora não
          </button>
        </div>

        <div className="h-[env(safe-area-inset-bottom)] sm:hidden" />
      </div>
    </div>
  );
}
